// Transport for the public shared-trip view: the anonymous page never touches
// AppSync (every table is userPool-authed) -- it fetches the share-trip
// Lambda's Function URL, which validates the unguessable share id and returns
// a whitelisted {name, legs, places}. Same outputs pattern as the weather
// proxy: the URL is baked into amplify_outputs.json per branch, read at call
// time so tests can swap it, and absent until that backend is deployed --
// shareTripAvailable() gates the owner-side Share button off in that case
// (frontend can ship first).

import outputs from '../amplify_outputs.json';
import {
  getTripShareForGroup,
  deleteTripShare,
  listTripSharesForItem,
  updateTripShareGroupKey,
} from './Database.mjs';

function shareTripBase() {
  return outputs?.custom?.shareTripFunctionUrl || '';
}

export function shareTripAvailable() {
  return Boolean(shareTripBase());
}

// Resolves to {status: 'ok', name, legs, places} or {status: 'gone'} (revoked,
// never issued, or the trip no longer exists -- the viewer can't tell and
// neither can we). Throws on network failures and server errors, which the
// page reports as "try again later" rather than "gone".
export async function fetchSharedTrip(shareId) {
  const base = shareTripBase();
  if (!base) throw new Error('Trip sharing is not configured.');
  const query = new URLSearchParams({ shareId }).toString();
  const response = await fetch(`${base}?${query}`);
  if (response.status === 404 || response.status === 410) return { status: 'gone' };
  if (!response.ok) throw new Error(`Shared trip request returned ${response.status}`);
  const body = await response.json();
  return {
    status: 'ok',
    name: body?.name ?? '',
    legs: body?.legs ?? [],
    places: body?.places ?? [],
  };
}

// Best-effort revoke when the trip a share points at is deleted.
//
// The shared page already answers "no longer available" for a group with no
// legs, so this is not what stops a link to a deleted trip from resolving --
// the Lambda is. What it stops is a link being INHERITED: a legacy 'n:' key is
// nothing but a name, so a trip created later and spelled the same way would
// pick up the old share row and publish itself to whoever held the link. (A
// 'g:' key is a fresh uuid and can't be re-minted, so there this really is
// tidying.)
//
// BEST-EFFORT, the same word and the same reason as
// removeTripWeatherSubscriptionsForGroup: the legs are already gone by the time
// this runs, so failing here must not turn a completed delete into an error the
// user is invited to retry. Returns the number of rows revoked.
// The rows a merge onto `newKey` touches, keeper first.
//
// Split out from the repoint below because the dialog asks the same question
// BEFORE the merge -- "will this revoke a link?" is only useful while it can
// still be answered no -- and a second copy of the keeper rule in the page
// would be free to disagree with the one that does the deleting.
//
// A row already on `newKey` wins; otherwise the first key in `oldKeys` does,
// and callers pass the trip the user is standing on first, so the link they
// published for THIS trip is the one that keeps working. Deduped by id because
// the same row would otherwise appear twice when newKey is one of oldKeys.
export function tripShareRowsForMerge(rows, oldKeys, newKey) {
  const keys = [newKey, ...(oldKeys || [])];
  return keys
    .flatMap((key) => (rows || []).filter((row) => row?.groupKey === key))
    .filter((row, index, all) => all.findIndex((r) => r?.id === row?.id) === index);
}

// Move the share rows of two trips that have just become one onto `newKey`.
//
// AT MOST ONE ROW may end up on a key, which is ShareTripModal's stated
// invariant and getTripShareForGroup's actual behaviour (it answers with
// items[0]). Two rows on one key is a live public URL its owner can no longer
// see or revoke, so the extras are deleted rather than left.
//
// Which one survives is not arbitrary: a row already on `newKey` wins, and
// otherwise the first key in `oldKeys` does -- callers pass the trip the user
// is standing on first, so the link they published for THIS trip is the one
// that keeps working.
//
// Doing nothing instead would be worse than untidy. A stranded 'g:' key answers
// "no longer available" forever with no way to clean it up; a stranded legacy
// 'n:' key can be resolved BY NAME onto the merged trip, so whoever holds the
// old link would silently be shown a trip they were never given.
//
// Best-effort, like every other cleanup here: the merge has already landed by
// the time this runs. Returns { repointed, revoked } so the caller can say what
// happened; a failure is swallowed and logged and reports what it managed.
export async function repointTripShares(itemId, oldKeys, newKey) {
  if (!shareTripAvailable()) return { repointed: 0, revoked: 0 };
  const result = { repointed: 0, revoked: 0 };
  try {
    const rows = await listTripSharesForItem(itemId);
    const matching = tripShareRowsForMerge(rows, oldKeys, newKey);
    if (matching.length === 0) return result;

    const [keep, ...extras] = matching;
    for (const row of extras) {
      await deleteTripShare(row.id);
      result.revoked += 1;
    }
    if (keep.groupKey !== newKey) {
      await updateTripShareGroupKey(keep.id, newKey);
      result.repointed += 1;
    }
    return result;
  } catch (err) {
    console.error('Combined the trips but could not update their share links', err);
    return result;
  }
}

export async function removeTripShareForGroup(itemId, groupKey) {
  if (!shareTripAvailable()) return 0;
  try {
    const share = await getTripShareForGroup(itemId, groupKey);
    if (!share) return 0;
    await deleteTripShare(share.id);
    return 1;
  } catch (err) {
    console.error('Deleted the trip but could not revoke its share link', err);
    return 0;
  }
}
